"use client";

import { ArrowUpRightIcon, ClockIcon, CopyIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../../cn";
import { AvatarStack, AvatarTile } from "../avatar-tile";
import { Badge } from "../badge";
import { BoardCard } from "../board";
import { Button } from "../button";
import { MetaCounts } from "../list-row";
import {
  countItems,
  DueLabel,
  PriorityMark,
  StatusBadge,
  StatusGlyph,
  TaskLabel,
  type TaskSummary,
  toAvatar,
} from "./task-meta";

// One task, two shapes. `board`: the §6.5 `BoardCard` (title clamp, ≤ 3 label chips, footer of
// avatars, due, priority and counts; the column already says the status, as in Work OS). `inline`: the live task card inside a chat message or an
// approval (Work OS messages-view TaskCardView): an `article` named "Task: …" with the status
// badge, location, assignees, due and an Open button.

export type TaskCardProps = Omit<React.ComponentProps<"article">, "title" | "onSelect"> & {
  task: TaskSummary;
  variant?: "board" | "inline" | undefined;
  onOpen?: ((taskId: string) => void) | undefined;
  /** Router link for the title (board) or Open button (inline). */
  render?: React.ReactElement | undefined;
  selected?: boolean | undefined;
  dragging?: boolean | undefined;
  ghost?: boolean | undefined;
  /** PARITY-0: a proposal waits on this task ("Move proposed", awaiting "Dana"). */
  pending?: TaskPending | undefined;
  /** PARITY-0: this card mirrors a task that lives on another board. */
  mirrorOf?: TaskMirrorOf | undefined;
};

export type TaskPending = { label: string; awaiting?: string | undefined };
export type TaskMirrorOf = { boardName: string; homeStatus: string };

/**
 * PARITY-0: the card's state markers, as badges beside its labels: a pending proposal (warning
 * tone, "Move proposed · awaiting Dana") and a mirror (info tone, "Mirror · Recruiting", its
 * home status on hover and to screen readers). Nothing when neither is given.
 */
function markers(pending: TaskPending | undefined, mirrorOf: TaskMirrorOf | undefined) {
  const out: React.ReactElement[] = [];
  if (pending) {
    const text = pending.awaiting
      ? `${pending.label} · awaiting ${pending.awaiting}`
      : pending.label;
    out.push(
      <Badge
        key="pending"
        variant="warning"
        data-slot="task-card-pending"
        aria-label={`Pending proposal: ${text}`}
        title={`Pending proposal: ${text}`}
      >
        <ClockIcon aria-hidden={true} />
        {text}
      </Badge>,
    );
  }
  if (mirrorOf) {
    const name = `Mirror of a task on ${mirrorOf.boardName} (${mirrorOf.homeStatus} there)`;
    out.push(
      <Badge
        key="mirror"
        variant="info"
        data-slot="task-card-mirror"
        aria-label={name}
        title={name}
      >
        <CopyIcon aria-hidden={true} />
        Mirror · {mirrorOf.boardName}
      </Badge>,
    );
  }
  return out;
}

const people = (task: TaskSummary) =>
  (task.assignees ?? []).map((p) => ({ ...toAvatar(p), title: p.title ?? p.name }));

/**
 * WORK-OS: who holds the card. One assignee reads as a tile and a short name (Linear's shape,
 * the full name and seat on hover); several as a stack whose tiles each name their person.
 */
function Assignees({ task }: { task: TaskSummary }) {
  const list = task.assignees ?? [];
  const one = list.length === 1 ? list[0] : undefined;
  if (one)
    return (
      <span
        data-slot="task-card-assignee"
        className="flex min-w-0 items-center gap-1.5"
        title={one.title ?? one.name}
      >
        <AvatarTile size="xs" {...toAvatar(one)} />
        <span className="max-w-24 truncate">{one.label ?? one.name}</span>
      </span>
    );
  return <AvatarStack items={people(task)} title={list.map((a) => a.title ?? a.name).join(", ")} />;
}

export function TaskCard({
  task,
  variant = "board",
  onOpen,
  render,
  selected,
  dragging,
  ghost,
  pending,
  mirrorOf,
  className,
  ...props
}: TaskCardProps) {
  const counts = countItems(task);
  const marks = markers(pending, mirrorOf);
  if (task.locked) {
    const who = `${task.locked.by} is working on this since ${task.locked.since}`;
    marks.unshift(
      <Badge
        key="locked"
        variant="neutral"
        data-slot="task-card-locked"
        aria-label={`AG working: ${who}`}
        title={who}
      >
        AG working
      </Badge>,
    );
  }
  if (variant === "board") {
    // The markers lead the chips (they say what state the card is in; labels follow).
    const chips = [
      ...marks,
      ...(task.labels ?? []).map((l) => <TaskLabel key={l.label} label={l} />),
    ];
    return (
      <BoardCard
        data-task-id={task.id}
        data-done={task.done || undefined}
        title={task.title}
        {...(render ? { render } : task.href ? { href: task.href } : {})}
        {...(onOpen ? { onSelect: () => onOpen(task.id) } : {})}
        {...(chips.length ? { chips } : {})}
        {...(task.assignees?.length ? { avatars: <Assignees task={task} /> } : {})}
        {...(task.due ? { due: <DueLabel due={task.due} overdue={task.overdue} /> } : {})}
        {...(task.priority ? { priority: <PriorityMark priority={task.priority} /> } : {})}
        {...(counts.length ? { meta: <MetaCounts items={counts} /> } : {})}
        {...(task.covers?.length ? { covers: task.covers } : {})}
        selected={selected ?? false}
        dragging={dragging ?? false}
        ghost={ghost ?? false}
        data-locked={task.locked ? "" : undefined}
        className={cn(
          "data-done:[&_[data-slot=board-card-title]]:text-muted-foreground data-done:[&_[data-slot=board-card-title]]:line-through",
          task.locked && "opacity-60",
          className,
        )}
        {...props}
      />
    );
  }
  return (
    <article
      data-slot="task-card"
      data-variant="inline"
      data-task-id={task.id}
      aria-label={`Task: ${task.title}`}
      className={cn(
        "flex flex-col gap-2 rounded-lg border border-border bg-card p-3 text-card-foreground",
        selected && "border-primary bg-primary-soft",
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 items-start gap-2">
        <StatusGlyph task={task} className="mt-0.5 shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span
            className={cn(
              "line-clamp-2 text-base font-medium text-foreground",
              task.done && "text-muted-foreground line-through",
            )}
          >
            {task.title}
          </span>
          {task.location ? (
            <span className="truncate text-xs text-muted-foreground">{task.location}</span>
          ) : null}
        </div>
        {onOpen || render || task.href ? (
          <Button
            variant="secondary"
            size="sm"
            className="shrink-0"
            aria-label={`Open task: ${task.title}`}
            {...(render
              ? { render, nativeButton: false }
              : task.href && !onOpen
                ? // biome-ignore lint/a11y/useAnchorContent: the Button's children and aria-label name it.
                  { render: <a href={task.href} />, nativeButton: false }
                : { onClick: () => onOpen?.(task.id) })}
          >
            Open
            <ArrowUpRightIcon data-icon="inline-end" />
          </Button>
        ) : null}
      </div>
      <div
        className={cn(
          "flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs tabular-nums",
          selected ? "text-secondary" : "text-muted-foreground",
        )}
      >
        {task.status ? <StatusBadge status={task.status} /> : null}
        {marks}
        {task.assignees?.length ? <AvatarStack items={people(task)} /> : null}
        {task.due ? <DueLabel due={task.due} overdue={task.overdue} /> : null}
        {task.priority ? <PriorityMark priority={task.priority} showLabel /> : null}
        {counts.length ? <MetaCounts items={counts} className="ml-auto" /> : null}
      </div>
    </article>
  );
}
